'use client';

import { ChevronDown, Download, Eye, FileText } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@govinity/ui/components/ui/button';
import { Card, CardContent } from '@govinity/ui/components/ui/card';
import { LoadingDialog } from '@govinity/ui/components/ui/loading-dialog';
import { LoadingSpinner } from '@govinity/ui/components/ui/loading-spinner';
import { CopyToClipboardButton } from '@govinity/ui/components/ui-custom/copy-to-clipboard-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@govinity/ui/components/ui/dropdown-menu';
import {
  type AttachmentTranslationUnsupportedReason,
  MeetingEventDocumentType,
  SecurityLevel,
} from '@govinity/validation';
import { downloadDocument, withFileExtension } from '@govinity/ui/lib/utils';
import { useToast } from '@govinity/ui/hooks/use-toast';
import {
  type TranslatableDocument,
  useDocumentTranslations,
} from '@/components/ui/translatable-documents';
import { useAuthStore } from '@/stores/authStore';

import { DocumentTranslationStatus } from './document-translation-status';

type Document = TranslatableDocument & {
  extension: string;
  name: string;
  securityLevel?: SecurityLevel;
  signedUrl: string;
  sourceSignedUrl?: string;
  type?: string;
  customType?: string | null;
};

const PERMANENT_LINK_SEGMENTS = {
  meeting: 'meetings',
  topic: 'topics',
} as const;

type PermanentLink = { id: number; type: keyof typeof PERMANENT_LINK_SEGMENTS };

type DocumentCardProps = {
  doc: Document;
  onView?: () => void;
  /** Optional number/label rendered before the name (kept separate so `doc.name` stays the raw filename). */
  numberLabel?: string | null;
  permanentLink?: PermanentLink;
};

export function DocumentCard({ doc, onView, numberLabel, permanentLink }: DocumentCardProps) {
  const t = useTranslations('DocumentCard');
  const permanentUrl = usePermanentUrl(doc, permanentLink);
  const { request } = useDocumentTranslations();
  const documentName = getDocumentName(t, doc);
  const displayLabel = numberLabel ? `${numberLabel} ${documentName}` : documentName;

  const handleView = () => {
    request(doc);
    onView?.();
  };

  return (
    <Card className="hover:bg-accent transition-colors">
      <CardContent>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
              <FileText className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                <p className="text-foreground min-w-0 text-sm font-medium break-words">
                  {numberLabel && <span className="me-1">{numberLabel}</span>}
                  {documentName}
                </p>
                <DocumentTranslationStatus
                  status={doc.translationStatus}
                  reason={doc.translationReason}
                />
              </div>
              <p className="text-muted-foreground text-xs">
                {doc.extension.toUpperCase()} • {t('documentLabel')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:shrink-0">
            {onView && (
              <Button
                aria-label={t('viewAriaLabel', { documentName: displayLabel })}
                className="h-9 min-w-0 flex-1 shrink sm:h-7 sm:flex-none sm:shrink-0"
                onClick={handleView}
                size="sm"
                variant="ghost"
              >
                <Eye className="mr-1 h-4 w-4" aria-hidden="true" />
                {t('viewButton')}
              </Button>
            )}
            <DownloadActions doc={doc} label={displayLabel} />
            {permanentUrl && (
              <CopyToClipboardButton
                className="h-9 w-auto min-w-0 flex-1 shrink px-3 sm:h-7 sm:flex-none sm:shrink-0"
                size="sm"
                stringToCopy={permanentUrl}
                variant="ghost"
                hideTooltip={true}
              >
                <span className="ml-1">{t('copyLinkButton')}</span>
              </CopyToClipboardButton>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Topic documents carry no `securityLevel` since the API only returns public ones.
function usePermanentUrl({ id, securityLevel }: Document, permanentLink?: PermanentLink) {
  const user = useAuthStore((state) => state.user);
  const { code } = useParams<{ code: string }>();

  if (
    typeof window === 'undefined' ||
    !permanentLink ||
    !user ||
    (securityLevel !== undefined && securityLevel !== SecurityLevel.PUBLIC)
  ) {
    return null;
  }

  const segment = PERMANENT_LINK_SEGMENTS[permanentLink.type];

  return `${window.location.origin}/files/${code}/${segment}/${permanentLink.id}/documents/${id}`;
}

type DownloadActionsProps = {
  doc: Document;
  label: string;
};

// `failed` keeps the transient wording — it is the one state worth retrying.
const UNSUPPORTED_TOAST_KEYS = {
  not_pdf: 'translationNotPdfToast',
  scanned: 'translationScannedToast',
  too_long: 'translationTooLongToast',
} as const satisfies Record<AttachmentTranslationUnsupportedReason, string>;

/**
 * A reader in a translated locale always gets both choices, because the source document is the
 * official record and should never be reachable only by first downloading a translation. Asking
 * for the translated copy before it exists starts the translation and waits; the wait lives in a
 * dialog rather than the row, since growing the row shifts every card below it and the reader has
 * already committed to the download.
 */
function DownloadActions({ doc, label }: DownloadActionsProps) {
  const t = useTranslations('DocumentCard');
  const tPending = useTranslations('DocumentTranslationPending');
  const { toast } = useToast();
  const { request, isTranslating } = useDocumentTranslations();
  const { signedUrl, sourceSignedUrl, translationStatus, translationReason } = doc;

  // Distinguishes "this reader clicked download" from a translation already running for some
  // other reason, so an unrelated pending document never pops a dialog open.
  const [awaitingDownload, setAwaitingDownload] = useState(false);
  const translating = isTranslating(doc);

  const download = useCallback(
    async (url: string) => {
      try {
        await downloadDocument(url, withFileExtension(doc.name, doc.extension));
      } catch {
        toast({ title: t('downloadFailedToast'), variant: 'destructive' });
      }
    },
    [doc.extension, doc.name, t, toast]
  );

  // The wait can end without a translation: refused for capacity, or the job failed, or the
  // document turned out to be untranslatable. `sourceSignedUrl` only appears once a translated
  // copy is actually being served, so it is the one safe signal that the wait succeeded. Handing
  // over the untranslated file here would silently give the reader the language they did not ask
  // for, which is the whole thing this wait exists to avoid.
  useEffect(() => {
    if (!awaitingDownload || translating) return;
    setAwaitingDownload(false);

    if (sourceSignedUrl) {
      void download(signedUrl);
      return;
    }

    toast({
      title: t(
        translationReason
          ? UNSUPPORTED_TOAST_KEYS[translationReason]
          : 'translationUnavailableToast'
      ),
      variant: 'destructive',
    });
  }, [
    awaitingDownload,
    translating,
    sourceSignedUrl,
    signedUrl,
    translationReason,
    download,
    toast,
    t,
  ]);

  const requestTranslatedDownload = () => {
    const isRetryable = translationStatus === null || translationStatus === 'failed';
    if (isRetryable) request(doc);
    if (isRetryable || translating) {
      setAwaitingDownload(true);
      return;
    }
    void download(signedUrl);
  };

  const originalUrl = sourceSignedUrl ?? signedUrl;
  const isTranslatedServed = !!sourceSignedUrl;

  const escapeToOriginal = () => {
    setAwaitingDownload(false);
    void download(originalUrl);
  };

  // LoadingDialog brings its own spinner, so this passes only the copy the viewer's waiting panel
  // shows — same keys, so the two surfaces cannot drift.
  const waitDialog = awaitingDownload && (
    <LoadingDialog
      onClose={escapeToOriginal}
      text={
        <div className="space-y-2">
          <p className="text-foreground text-base font-medium">{tPending('title')}</p>
          <p className="text-sm">{tPending('description')}</p>
          <p className="text-xs">{tPending('disclaimer')}</p>
          <Button className="mt-2" onClick={escapeToOriginal} size="sm" variant="outline">
            {t('downloadOriginal')}
          </Button>
        </div>
      }
    />
  );

  // Outside a translated locale there is nothing to choose between, so the plain button stands.
  if (translationStatus === undefined) {
    return (
      <Button
        aria-label={t('downloadAriaLabel', { documentName: label })}
        className="h-9 min-w-0 flex-1 shrink sm:h-7 sm:flex-none sm:shrink-0"
        disabled={!signedUrl}
        onClick={() => void download(signedUrl)}
        size="sm"
        variant="ghost"
      >
        <Download className="mr-1 h-4 w-4" aria-hidden="true" />
        {t('downloadButton')}
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={t('downloadAriaLabel', { documentName: label })}
            className="h-9 min-w-0 flex-1 shrink sm:h-7 sm:flex-none sm:shrink-0"
            disabled={!signedUrl}
            size="sm"
            variant="ghost"
          >
            {translating ? (
              <LoadingSpinner size={16} className="mr-1" aria-hidden="true" />
            ) : (
              <Download className="mr-1 h-4 w-4" aria-hidden="true" />
            )}
            <span className="truncate">
              {translating ? t('preparingDownload') : t('downloadButton')}
            </span>
            <ChevronDown className="ms-1 h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => void download(originalUrl)}>
            {t('downloadOriginal')}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={translationStatus === 'unsupported'}
            onClick={
              isTranslatedServed ? () => void download(signedUrl) : requestTranslatedDownload
            }
          >
            {t('downloadTranslated')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {waitDialog}
    </>
  );
}

type DocumentCardTranslations = ReturnType<typeof useTranslations<'DocumentCard'>>;

/**
 * The label a document is known by across the portal. Surfaces that list documents outside a
 * `DocumentCard` — a reader's navigation, a viewer heading — take it from here so the same document
 * is never named one thing in a list and another once opened.
 */
export function useDocumentName() {
  const t = useTranslations('DocumentCard');

  return useCallback((doc: Document) => getDocumentName(t, doc), [t]);
}

const getDocumentName = (t: DocumentCardTranslations, doc: Document) => {
  switch (doc.type) {
    case MeetingEventDocumentType.AGENDA_OUTLINE:
      return t('agendaOutline');
    case MeetingEventDocumentType.AGENDA_PACKAGE:
      return t('agendaPackage');
    case MeetingEventDocumentType.MINUTES:
      return t('meetingMinutes');
    case MeetingEventDocumentType.TRANSCRIPT:
      return t('transcript');
    case MeetingEventDocumentType.PUBLIC_NOTICE:
      return t('publicNotice');
    default:
      return doc.customType || doc.name;
  }
};
